import * as Actions from '../actions/templateTaskActions';
import TemplateTask from '../../entities/templateTask';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type TemplateTaskAction = { 
    type: string; 
    templateTask: TemplateTask | null; 
    templateTaskId: string | TemplateTask; 
    partial: boolean; 
    lastFetch: number; 
    templateTasks: TemplateTask[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<TemplateTask> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {TemplateTask[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<TemplateTask> | null, firmId: string, userId: string, updates?: TemplateTask[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'templateTask', 'templateTasks', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<TemplateTask>} data
 * @param {TemplateTask[]} [updates]
 * @returns {ArrayMap<TemplateTask>}
 */
function updateStorage(data: ArrayMap<TemplateTask>, updates?: TemplateTask[]): ArrayMap<TemplateTask> {
	if (logging) console.debug('⮂ templateTaskReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {TemplateTask[]} [updates]
 * @returns {ArrayMap<TemplateTask>}
 */
function updateStorageNoReturn(updates?: TemplateTask[]): void {
	if (logging) console.debug('⮂ templateTaskReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {TemplateTask[]} [data]
 * @returns {ArrayMap<TemplateTask>}
 */
function createState(data?: TemplateTask[] | ArrayMap<TemplateTask>): ArrayMap<TemplateTask> {
	if (!data) return ArrayMapRegistry.clone<TemplateTask>('templateTask', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<TemplateTask>('templateTask', data.map(e => e));
    else return ArrayMapRegistry.clone<TemplateTask>('templateTask', data);
}

function templateTasks(state: ArrayMap<TemplateTask>, action: TemplateTaskAction): ArrayMap<TemplateTask> {
	if (!state) state = createState();

	let st: ArrayMap<TemplateTask>;

	switch (action.type) {
		// action.templateTask - the new value of templateTask with id action.id
		// action.templateTaskId - id of the target, used if new value is null
		case Actions.TEMPLATETASK_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ templateTaskReducer: FETCH_ONE_SUCCESS', action);

			if (action.templateTask === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.templateTaskId)) {
				st = state.clone();
				st.set(action.templateTask);
				// state.clear();

				updateStorageNoReturn([action.templateTask]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.templateTasks
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.templateTasks
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.templateTasks
			 !partial, !stateEmpty, fetchEmpty	-> D return templateTasks in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge templateTasks in state newer than those in action.templateTasks
		 */
		case Actions.TEMPLATETASK_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.templateTasks.length === 0;
			const fetched = action.templateTasks.map(templateTask => new TemplateTask({...templateTask, persisted: true}));

			if (logging) console.debug(`⮂ templateTaskReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} templateTasks with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ templateTaskReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ templateTaskReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<TemplateTask> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: TemplateTask[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(templateTask => {
					const current = st.get(templateTask.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && templateTask.status === 'D') {
							// Remove it from state
							toUpdate.push(templateTask);
							st.delete(templateTask);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (templateTask.addedOn > current.addedOn) {
								toUpdate.push(templateTask);
								st.set(templateTask);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (templateTask.status !== 'D') {
							toUpdate.push(templateTask);
							st.set(templateTask);
						}
						else if (logging) console.debug('⮂ templateTaskReducer: partialMerge: skipping deleted templateTask not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ templateTaskReducer: empty: templateTasks', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ templateTaskReducer: fullMerge: templateTasks', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localTemplateTask => {
                    // If this templateTask has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localTemplateTask.persisted) {
                        const fetchedTemplateTask = st.get(localTemplateTask.id);
                        if (!fetchedTemplateTask || localTemplateTask.addedOn > fetchedTemplateTask.addedOn) st.set(localTemplateTask);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.TEMPLATETASK_FETCH_ONE_FAILURE:
			console.error('TEMPLATETASK_FETCH_ONE_FAILURE', action.templateTaskId, action.err);

			// No update to storage required
			return state;

		case Actions.TEMPLATETASK_FETCH_FAILURE:
			console.error('TEMPLATETASK_FETCH_FAILURE', action.templateTaskId, action.err);

			// No update to storage required
			return state;

		case Actions.TEMPLATETASK_RESTORE: {
		    let templateTask = new TemplateTask({...action.templateTask});
            st = state.clone();

            const existing = st.get(templateTask.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (templateTask.addedOn >= existing.addedOn) {
                    st.set(templateTask);
                    return updateStorage(st, [templateTask]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (templateTask.status === 'D') return state;

                st.set(templateTask);
                return updateStorage(st, [templateTask]);
            }
        }
            
		case Actions.TEMPLATETASK_SAVE: {
		    // console.debug('⮂ TEMPLATETASK_SAVE reducer', action.templateTask?.id);
		    
			// clone the templateTask
			let templateTask = new TemplateTask({...action.templateTask});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new templateTask in the state
			// If this is a create, add to the queue
			const _templateTask = st.get(templateTask.id);

			if (!_templateTask) st.set(templateTask);
			else {
				if (templateTask.addedOn >= _templateTask.addedOn) st.set(templateTask);
			}

			return updateStorage(st, [templateTask]);
		}

		case Actions.TEMPLATETASK_SAVE_MANY: {
		    if (logging) console.debug('⮂ TEMPLATETASK_SAVE_MANY reducer', action.templateTasks.map(templateTask => templateTask.id).join(','));
		    
			// clone the templateTasks
			let toProcess = action.templateTasks.map(templateTask => new TemplateTask(templateTask));

			st = state.clone();
			// state.clear();

			const updates: TemplateTask[] = [];

			toProcess.forEach(templateTask => {
				// Add or update the new templateTask in the state
				// If this is a create, add to the queue
				const existing = st.get(templateTask.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (templateTask.status !== 'D') {
						st.push(templateTask);
						updates.push(templateTask);
					}
					else if (logging) console.debug('⮂ TEMPLATETASK_SAVE_MANY: skipping deleted templateTask not in state');
				}
				else {
					if (templateTask.addedOn >= existing.addedOn) {
						st.set(templateTask);
						updates.push(templateTask);
					}
					else if (logging) console.debug('⮂ Skipping, existing templateTask had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.TEMPLATETASK_REMOVE: {
			// clone the templateTask
			const templateTask = new TemplateTask({...action.templateTask});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(templateTask);

			return updateStorage(st, [templateTask]);
		}

		case Actions.TEMPLATETASK_RESTORE_FAILURE:
		case Actions.TEMPLATETASK_SAVE_FAILURE: {
		    console.warn('TEMPLATETASK_SAVE_FAILURE reducer', action.templateTask?.id);
		    if (!action.templateTask?.id) {
				console.warn('TEMPLATETASK_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const templateTask = ensure(TemplateTask, action.templateTask);
			if (!templateTask) {
				console.error('Could not find templateTask after call to save');
				return state;
			}

			st = state.clone();

			const _templateTaskRef = state.get(templateTask.id);

			if (_templateTaskRef && (_templateTaskRef.addedOn === templateTask.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _templateTask = new TemplateTask({..._templateTaskRef});
				_templateTask.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_templateTask.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('templateTask', _templateTask).then(r => {
						if (r.isErr()) console.error(`⮂ templateTaskReducer: failed to save templateTask with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ templateTaskReducer: saved templateTask with fatalError info');
					}).catch(err => console.error(`⮂ templateTaskReducer: failed to save templateTask with fatalError info: ${err.message}`));

				st.set(_templateTask);
			}

			return updateStorage(st);
		}

		case Actions.TEMPLATETASK_SAVE_MANY_FAILURE: {
		    console.warn('TEMPLATETASK_SAVE_MANY_FAILURE reducer', action.templateTasks.map(templateTask => templateTask.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.TEMPLATETASK_RESTORE_SUCCESS:
		case Actions.TEMPLATETASK_SAVE_SUCCESS: {
			if (!action.templateTask?.id) {
				console.warn('TEMPLATETASK_SAVE_SUCCESS reducer');
                return state;
			}

			const templateTaskRef = state.get(action.templateTask?.id);
			if (!templateTaskRef) {
				console.warn('Could not find templateTask after call to save');
                return state;
			}

			st = state.clone();

			if (action.templateTask.addedOn >= templateTaskRef.addedOn) {
				st.set(action.templateTask);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const templateTask = new TemplateTask({...templateTaskRef, persisted: true});
				st.set(templateTask);
			}

			return updateStorage(st, [action.templateTask]);
		}

		case Actions.TEMPLATETASK_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ TEMPLATETASK_SAVE_MANY_SUCCESS reducer', action.templateTasks.map(templateTask => templateTask.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const templateTasks: TemplateTask[] = [];

			action.templateTasks.forEach(e => {
				const templateTaskRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!templateTaskRef) console.warn('Could not find templateTask after call to save');
				else if (e.addedOn >= templateTaskRef.addedOn) {
				    st.set(e);
				    templateTasks.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const templateTask = new TemplateTask({...templateTaskRef, persisted: true});
					st.set(templateTask);
					templateTasks.push(templateTask);
				}
			});

			return updateStorage(st, templateTasks);
		}

		case Actions.TEMPLATETASK_REMOVE_FAILURE: {
		    console.warn('TEMPLATETASK_REMOVE_FAILURE action', action.templateTask?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.TEMPLATETASK_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.TEMPLATETASK_RESTORE_MANY: {
			if (logging) console.debug('⮂ TEMPLATETASK_RESTORE_MANY reducer', action.templateTasks.map(templateTask => templateTask.id).join(','));

			const toProcess = action.templateTasks.map(templateTask => new TemplateTask({...templateTask}));
			st = state.clone();

			const updates: TemplateTask[] = [];

			toProcess.forEach(templateTask => {
				const existing = st.get(templateTask.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (templateTask.addedOn >= existing.addedOn) {
						st.set(templateTask);
						updates.push(templateTask);
					}
					else if (logging) console.debug('⮂ TEMPLATETASK_RESTORE_MANY: skipping older version of templateTask');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (templateTask.status !== 'D') {
						st.set(templateTask);
						updates.push(templateTask);
					}
					else if (logging) console.debug('⮂ TEMPLATETASK_RESTORE_MANY: skipping deleted templateTask not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.TEMPLATETASK_REMOVE_MANY: {
			if (logging) console.debug('⮂ TEMPLATETASK_REMOVE_MANY reducer', action.templateTasks.map(templateTask => templateTask.id).join(','));

			const toProcess = action.templateTasks.map(templateTask => new TemplateTask({...templateTask}));
			st = state.clone();

			toProcess.forEach(templateTask => {
				st.delete(templateTask);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default templateTasks;